// third-party
import { createSlice } from '@reduxjs/toolkit';
import { addDays, format } from 'date-fns';

// project imports
import { ARYABHATTA_API_DOMAIN_V1 } from 'config';
import { AUTO_QUOTE, DEFAULT_ERROR_MESSAGE, HOTELS, OFFLINE_COMPONENT_URL, SIGHT_SEEING } from '../constant';
import { devConsolelog } from 'helpers/utilityFunctions';
import axios from 'utils/axios';
import { copyOfObject, HOTELS_AND_SIGHTSEEING, RESORT, ROOM_DATA, SIGHTSEEING_FIELD } from 'views/pages/create-quote/constants';
import { dispatch } from '../index';
import { openSnackbar } from './snackbar';
import { isEmptyObject } from 'helpers/objectFunctions';
import copyObjectAndRemoveKey, { convertUrl } from 'views/pages/create-quote/OfflineComponent/utils';
import { TRANSFERS_TYPE } from 'views/pages/create-quote/OfflineComponent/constants';
import { getCodeValue } from 'views/pages/internal-commenting/utils';
import { getFinalTransfersStr } from 'views/pages/create-quote/EditAllTransfers/utils';
import { setTransferData } from './transfers';
import { ONLINE_TRANSFERS_CREATE_QUOTE } from 'views/pages/create-quote/EditAllTransfers/constants';
import { checkIsKeyPresentInObject, getTransfersDataFromApi } from 'views/pages/create-quote/utilities';

// ----------------------------------------------------------------------

const initialState = {
    error: null,
    data: {
        formStructures: {},
        hotelIndex: 1
    },
    autoSuggestData: { hotels: [], areas: [], sightseeings: [], transfers: [], requirement: [] },
    autoSuggestInputValues: {},
    quotesList: {},
    destinationDetails: null,
    isLoading: null,
    quoteDetails: {},
    hotelDetails: {},
    isQuotesListLoading: false,
    isQuoteDetailsLoading: false,
    isPrefillDataLoading: null,
    isShareWithAgentLoading: false,
    successMessage: null,
    errorsOfFormBuilder: [],
    isLandOnly: { 0: false },
    err: { 0: {} },
    selectedTab: null,
    offlineHotelData: {},
    offlineSightSeeingData: [],
    offlineSightHotelDataLoading: false,
    offlineSightSeeingDataLoading: false
};

const slice = createSlice({
    name: 'quoteDetails',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },

        hasErrValidation(state, action) {
            const { error = {}, quoteTab = 0 } = action.payload || {};
            devConsolelog('inside reducer333', action.payload);
            state.err[quoteTab] = { ...state.err[quoteTab], ...error };
        },

        // setData
        setFormStructures(state, action) {
            const { formStructure = {}, quoteTabValue = null } = action.payload;
            const copyOfFormStructure = state.data.formStructures[quoteTabValue] || {};
            const newPrefilledFormStructure = { ...(copyOfFormStructure.prefilledFormStructure || {}), ...formStructure };
            const newFormStructure = { ...copyOfFormStructure, prefilledFormStructure: { ...newPrefilledFormStructure } };
            devConsolelog('new form strcurcutr', copyOfObject(newFormStructure));
            state.data.formStructures[quoteTabValue] = copyOfObject(newFormStructure) || {};
        },

        setLandOnlyState(state, action) {
            const { tabValue: quoteTabValue = null, isLandOnly = false } = action.payload;
            devConsolelog('inside land only quote222', state, action.payload);
            state.isLandOnly[quoteTabValue] = isLandOnly;
        },

        setOriginalFormStructure(state, action) {
            const { formStructure = {}, quoteTabValue = null } = action.payload;
            const copyOfFormStructure = state.data.formStructures[quoteTabValue] || {};
            const newPrefilledFormStructure = { ...(copyOfFormStructure.prefilledFormStructure || {}), ...formStructure };
            const obj = { originalFormStructure: formStructure, prefilledFormStructure: newPrefilledFormStructure };

            const { form_field_groups: formFieldGroups = [] } = newPrefilledFormStructure || {};
            const hotelAndSightSeeingArray = formFieldGroups.filter((field) => field.key === 'hotel_and_sightseeings');
            hotelAndSightSeeingArray.forEach((hotels, index) => {
                hotels.hotel_index = index + 1;
            });
            newPrefilledFormStructure.rooms_guests = [ROOM_DATA];
            newPrefilledFormStructure.is_vip ??= false;
            state.data.formStructures[quoteTabValue] = obj;
            state.data.hotelIndex = hotelAndSightSeeingArray.length;
            devConsolelog('initial form structure', newPrefilledFormStructure, hotelAndSightSeeingArray);
        },

        // hotel index number
        setHotelIndex(state, action) {
            state.data.hotelIndex = action.payload;
        },

        setSelectedTab(state, action) {
            state.selectedTab = action.payload;
        },

        setQuotesList(state, action) {
            state.quotesList = action.payload;
            state.isQuotesListLoading = false;
        },

        setNewAddedQuote(state, action) {
            state.quotesList = { ...action.payload, ...state.quotesList };
        },

        setIsLoading(state, action) {
            const { loadingField = '', isLoading = false } = action.payload || {};
            state[loadingField] = isLoading;
        },

        setQuoteDetails(state, action) {
            state.quoteDetails = action.payload;
            state.isQuoteDetailsLoading = false;
        },

        setAutoSuggestData(state, action) {
            const { results = [], key = '', type = null, index = null } = action.payload;
            if (type === 'requirement') state.autoSuggestData[type] = results;
            else {
                const fieldsArr = state.autoSuggestData[type] || [];
                const particularField = fieldsArr[index] || {};
                const newField = { ...particularField, ...{ [key]: results } };
                fieldsArr[index] = newField;
                state.autoSuggestData[type] = fieldsArr;
            }
        },

        setAutoSuggestInputValues(state, action) {
            const { key, value, index, tabValue, type } = action.payload;
            const copyInputValues = copyOfObject(state.autoSuggestInputValues);
            const inputTabFields = copyInputValues[tabValue] || {};
            const inputTypeFields = inputTabFields[type] || [];
            let fieldTypeIndex = inputTypeFields[index] || {};
            fieldTypeIndex = { ...fieldTypeIndex, ...{ [key]: value } };
            inputTypeFields[index] = fieldTypeIndex;
            inputTabFields[type] = inputTypeFields;
            copyInputValues[tabValue] = inputTabFields;
            state.autoSuggestInputValues = copyInputValues;
        },

        addHotelFormFieldGroups(state, action) {
            const { formStructureAfterAddingHotel = {}, tabValue = null } = action.payload;
            const formStructure = { ...(state.data.formStructures[tabValue] || {}) };
            formStructure.prefilledFormStructure = formStructureAfterAddingHotel;
            state.data.formStructures[tabValue] = formStructure;
        },

        setDestinationDetails(state, action) {
            state.destinationDetails = action.payload;
            state.data.formStructures = {};
        },

        setHotelDetailsData(state, action) {
            state.hotelDetails = action.payload;
        },

        setErrorsOfFormBuilder(state, action) {
            state.errorsOfFormBuilder = action.payload;
        },

        setPrefillFormStructure(state, action) {
            const { formStructure = {}, quoteTabValue = null } = action.payload;
            const newFormData = copyOfObject(formStructure);
            const { prefilledFormStructure = {} } = newFormData || {};
            const { form_field_groups: formFieldGroups = [] } = prefilledFormStructure || {};
            const hotelAndSightSeeingArray = formFieldGroups.filter((field) => field.key === 'hotel_and_sightseeings');
            hotelAndSightSeeingArray.forEach((hotels, index) => {
                hotels.hotel_index = index + 1;
            });
            // dispatch(slice.actions.setHotelId({ formStructure: newFormData, quoteTabValue }));
            state.data.formStructures[quoteTabValue] = copyOfObject(newFormData) || {};
            state.data.hotelIndex = hotelAndSightSeeingArray.length;
        },

        setSuccessMessage(state, action) {
            state.successMessage = action.payload;
        },
        toggleOfflineComponent(state) {
            return { ...state, showOfflineComponent: !state.showOfflineComponent };
        },
        updateHotelName(state, action) {
            const {
                tabValue,
                hotelIndex,
                offlineComponentPayload,
                resetToOriginalState = false,
                isOfflineSubmit = true,
                isHhiForm = false
            } = action?.payload;
            const formFieldGroups = state?.data?.formStructures?.[tabValue]?.prefilledFormStructure?.form_field_groups || {};
            devConsolelog('action21313', JSON.parse(JSON.stringify(state?.data)), action?.payload);
            const hotelForms =
                formFieldGroups &&
                formFieldGroups?.find(
                    (field) => (field?.key === HOTELS_AND_SIGHTSEEING || field?.key === RESORT) && field?.hotel_index === hotelIndex
                );
            const hotelFields = hotelForms?.fields;
            const hotelField = hotelFields?.find((fieldItem) => fieldItem?.field?.key === 'hotel_code');
            devConsolelog('hotelField8213', JSON.parse(JSON.stringify(hotelField)));

            const roomTypeField = hotelFields?.find((field) => {
                const { field: innerField = {} } = field || {};
                const { key = null } = innerField || {};
                return key === 'room_code_1';
            });
            const nightsField = hotelFields?.find((field) => {
                const { field: innerField = {} } = field || {};
                const { key = null } = innerField || {};
                return key === 'number_of_nights_1';
            });
            const areaField = hotelFields?.find(
                (fieldItem) => fieldItem?.field?.key === 'area' || fieldItem?.field?.key === 'type_of_stay'
            );

            const mealPlanField = hotelFields?.find((fieldItem) => fieldItem?.field?.key === 'meal_type');
            const childWithBedField = hotelFields?.find(
                (fieldItem) => fieldItem?.field?.key === 'is_child_with_bed' || fieldItem?.field?.key === 'type_of_stay'
            );

            if (resetToOriginalState) {
                if (isHhiForm) {
                    areaField.field.input_value = {};
                    hotelField.field.input_value = {};
                    roomTypeField.field.input_value = [];
                    nightsField.field.input_value = {};
                    mealPlanField.field.input_value = [];
                    if (hotelForms?.isOccupancyChanged) {
                        delete hotelForms.isOccupancyChanged;
                    }
                    if (hotelForms?.isQuoteTypeChanged) {
                        delete hotelForms.isQuoteTypeChanged;
                    }
                } else {
                    areaField.field.input_value = {
                        label: '',
                        value: '',
                        code: '',
                        isOfflineComponent: false
                    };
                    hotelField.field.input_value = {
                        label: '',
                        value: '',
                        code: '',
                        isOfflineComponent: false
                    };
                    roomTypeField.field.input_value = {
                        label: '',
                        value: '',
                        key: '',
                        isOfflineComponent: false
                    };
                    nightsField.field.input_value = {
                        label: '',
                        value: '',
                        key: '',
                        isOfflineComponent: false
                    };
                    childWithBedField.field.input_value = {
                        label: 'Child With Bed',
                        value: false,
                        key: 'is_child_with_bed',
                        isOfflineComponent: false
                    };
                }
            } else if (isOfflineSubmit) {
                if (isHhiForm) {
                    devConsolelog('roomTypeField', roomTypeField);

                    hotelField.field.input_value = {
                        label: offlineComponentPayload?.name || '',
                        value: offlineComponentPayload?.code || '',
                        remarks: offlineComponentPayload?.remarks || '',
                        key: offlineComponentPayload?.code || '',
                        isOfflineComponent: true,
                        starRating: offlineComponentPayload?.starRating || 0,
                        prefilledOfflineData: { ...(copyObjectAndRemoveKey(offlineComponentPayload, 'area') || {}) }
                    };
                    areaField.field.input_value = {
                        ...(offlineComponentPayload?.area || {})
                    };
                    roomTypeField.field.input_value = [
                        {
                            label: offlineComponentPayload?.roomType || '',
                            value: offlineComponentPayload?.roomCode || '',
                            key: offlineComponentPayload?.roomCode || '',
                            isOfflineComponent: true,
                            rateId: '',
                            roomId: ''
                        }
                    ];
                    mealPlanField.field.input_value = [
                        {
                            label: offlineComponentPayload?.mealType || '',
                            value: offlineComponentPayload?.mealType || ''
                        }
                    ];

                    nightsField.field.input_value = {
                        label: offlineComponentPayload?.numberOfNight || '',
                        value: offlineComponentPayload?.numberOfNight || '',
                        key: offlineComponentPayload?.numberOfNight || '',
                        isOfflineComponent: true
                    };
                    if (hotelForms?.isOccupancyChanged) {
                        delete hotelForms.isOccupancyChanged;
                    }
                    if (hotelForms?.isQuoteTypeChanged) {
                        delete hotelForms.isQuoteTypeChanged;
                    }
                } else {
                    hotelField.field.input_value = {
                        label: offlineComponentPayload?.name || '',
                        value: offlineComponentPayload?.code || '',
                        key: offlineComponentPayload?.code || '',
                        isOfflineComponent: true,
                        remarks: offlineComponentPayload?.remarks || '',
                        prefilledOfflineData: { ...(copyObjectAndRemoveKey(offlineComponentPayload, 'area') || {}) }
                    };
                    areaField.field.input_value = {
                        ...(offlineComponentPayload?.area || {})
                    };

                    roomTypeField.field.input_value = {
                        label: offlineComponentPayload?.roomType || '',
                        value: offlineComponentPayload?.roomCode || '',
                        key: offlineComponentPayload?.roomCode || '',
                        isOfflineComponent: true
                    };

                    nightsField.field.input_value = {
                        label: offlineComponentPayload?.numberOfNight || '',
                        value: offlineComponentPayload?.numberOfNight || '',
                        key: offlineComponentPayload?.numberOfNight || '',
                        isOfflineComponent: true
                    };
                    childWithBedField.field.input_value = {
                        label: 'Child With Bed',
                        value: offlineComponentPayload?.childWithBed,
                        key: 'is_child_with_bed',
                        isOfflineComponent: true
                    };
                }
            } else if (isHhiForm) {
                areaField.field.input_value = {};
                hotelField.field.input_value = {};
                roomTypeField.field.input_value = [];
                nightsField.field.input_value = {};
                mealPlanField.field.input_value = [];
            } else {
                areaField.field.input_value = {
                    label: '',
                    value: '',
                    code: '',
                    isOfflineComponent: false
                };
                hotelField.field.input_value = {
                    label: '',
                    value: '',
                    code: '',
                    isOfflineComponent: false
                };
                roomTypeField.field.input_value = {
                    label: '',
                    value: '',
                    key: '',
                    isOfflineComponent: false
                };
                nightsField.field.input_value = {
                    label: '',
                    value: '',
                    key: '',
                    isOfflineComponent: false
                };
                childWithBedField.field.input_value = {
                    label: 'Child With Bed',
                    value: false,
                    key: 'is_child_with_bed',
                    isOfflineComponent: false
                };
            }
        },

        saveOfflinehotelData(state, action) {
            return { ...state, offlineHotelData: { ...(action?.payload || {}) } };
        },
        saveOfflineSightSeeingData(state, action) {
            return { ...state, offlineSightSeeingData: [...(action?.payload || [])] };
        },
        setOfflineHotelDataLoading(state, action) {
            return { ...state, offlineSightHotelDataLoading: action.payload };
        },
        setOfflineSightSeeingDataLoading(state, action) {
            return { ...state, offlineSightSeeingDataLoading: action.payload };
        },
        findAndsetOfflineSightSeeingForHotelCard(state, action) {
            const { tabValue, hotelIndex, offlineSightSeeingData = [] } = action?.payload || {};
            const formFieldGroups = state?.data?.formStructures?.[tabValue]?.prefilledFormStructure?.form_field_groups || {};
            const hotelForms =
                formFieldGroups && formFieldGroups.filter((field) => field?.key === HOTELS_AND_SIGHTSEEING || field?.key === RESORT);

            const hotelForm = hotelForms.find((form) => form.hotel_index === hotelIndex) || hotelForms[0];
            const sightseeingField =
                hotelForm.fields.find((item) => {
                    const { field = {} } = item || {};
                    const { key = null } = field || {};
                    return key === SIGHTSEEING_FIELD;
                }) || {};
            const { field = {} } = sightseeingField || {};
            const { input_value: sightSeeingsValue = [] } = field || {};
            if (Array?.isArray(sightSeeingsValue) && offlineSightSeeingData?.length > 0) {
                const filterOutNonOfflineSightSeeings = sightSeeingsValue?.filter((i) => !i?.isOfflineComponent);
                field.input_value = [...filterOutNonOfflineSightSeeings, ...offlineSightSeeingData];
            }
        },
        setOnlineHotel(state, action) {
            const { tabValue, hotelIndex, roomFieldData, mealFieldData, areaObj, hotelName, numberOfnights, traceId, providerTraceId } =
                action?.payload;

            const formFieldGroups = state?.data?.formStructures?.[tabValue]?.prefilledFormStructure?.form_field_groups || {};
            const prefilledReference = state?.data?.formStructures?.[tabValue]?.prefilledFormStructure;
            prefilledReference.traceId = traceId;
            prefilledReference.providerTraceId = providerTraceId;

            devConsolelog('action21313', JSON.parse(JSON.stringify(state?.data)), action?.payload);
            const hotelForms =
                formFieldGroups &&
                formFieldGroups?.find(
                    (field) => (field?.key === HOTELS_AND_SIGHTSEEING || field?.key === RESORT) && field?.hotel_index === hotelIndex
                );
            devConsolelog('hotel22Forms', hotelForms);
            if (hotelForms?.isOccupancyChanged) {
                delete hotelForms.isOccupancyChanged;
            }
            if (hotelForms?.isQuoteTypeChanged) {
                delete hotelForms.isQuoteTypeChanged;
            }
            const hotelFields = hotelForms?.fields;
            const hotelField = hotelFields?.find((fieldItem) => fieldItem?.field?.key === 'hotel_code');
            devConsolelog('hotelField8213', JSON.parse(JSON.stringify(hotelField)));

            const roomTypeField = hotelFields?.find((field) => {
                const { field: innerField = {} } = field || {};
                const { key = null } = innerField || {};
                return key === 'room_code_1';
            });
            const nightsField = hotelFields?.find((field) => {
                const { field: innerField = {} } = field || {};
                const { key = null } = innerField || {};
                return key === 'number_of_nights_1';
            });
            const areaField = hotelFields?.find(
                (fieldItem) => fieldItem?.field?.key === 'area' || fieldItem?.field?.key === 'type_of_stay'
            );
            const mealPlanField = hotelFields?.find((fieldItem) => fieldItem?.field?.key === 'meal_type');

            roomTypeField.field.input_value = roomFieldData;
            mealPlanField.field.input_value = mealFieldData;
            areaField.field.input_value = areaObj;
            nightsField.field.input_value = numberOfnights;
            hotelField.field.input_value = hotelName;
        },
        setFormDataProviderTraceId(state, action) {
            const { tabValue, providerTraceId } = action?.payload;
            const prefilledReference = state?.data?.formStructures?.[tabValue]?.prefilledFormStructure;
            prefilledReference.providerTraceId = providerTraceId;
        },
        setQuoteTransferFromFields(state, action) {
            try {
                const { tabValue, apiData } = action?.payload;
                const prefilledReference = state?.data?.formStructures?.[tabValue]?.prefilledFormStructure;
                const formFieldGroups = prefilledReference?.form_field_groups || [];
                const transferFormField = getCodeValue(formFieldGroups, 'key', 'transfers', 'fields');

                const transferTypeField = transferFormField?.find((i) => i?.field?.key === 'transfer_type');
                const pickUpFrom = transferFormField?.find((i) => i?.field?.key === 'pickup_from');
                const dropAt = transferFormField?.find((i) => i?.field?.key === 'drop_at');
                const cabTypeField = transferFormField?.find((i) => i?.field?.key === 'cab_type');
                const numberOfCabField = transferFormField?.find((i) => i?.field?.key === 'number_of_cabs');

                const setFieldValue = (obj, value) => {
                    if (!obj) return;
                    obj.field.input_value = {
                        ...(obj?.field?.input_value || {}),
                        ...(value || {})
                    };
                };
                const getDataFromApiResponse = (apiData = '') => {
                    let transferType = '';
                    let pickup = '';
                    let dropOf = '';
                    let cabType = '';
                    let numberOfcabs = 0;
                    console.log('apiData---->apiData', apiData);

                    if (apiData && typeof apiData === 'object' && Object?.keys(apiData)?.length > 0) {
                        const finalRouteData = getFinalTransfersStr(apiData);
                        transferType = {
                            label: apiData?.finalTransferType,
                            key: apiData?.finalTransferType,
                            value: apiData?.finalTransferType
                        };
                        pickup = { label: finalRouteData?.[0]?.pickUpPoint, value: finalRouteData?.[0]?.originCode };
                        dropOf = {
                            label: finalRouteData?.[finalRouteData?.length - 1]?.dropPoint,
                            value: finalRouteData?.[finalRouteData?.length - 1]?.destinationCode,
                            key: finalRouteData?.[finalRouteData?.length - 1]?.destinationCode
                        };
                        cabType = {
                            label: apiData?.transferMode?.name || '',
                            value: apiData?.transferMode?.code || '',
                            key: apiData?.transferMode?.code || ''
                        };
                        numberOfcabs = {
                            label: apiData?.numberOfCabs || '',
                            value: apiData?.numberOfCabs || '',
                            key: apiData?.numberOfCabs || ''
                        };
                    }
                    return {
                        transferType,
                        pickup,
                        dropOf,
                        cabType,
                        numberOfcabs
                    };
                };

                const { transferType = '', pickup = '', dropOf = '', cabType = '', numberOfcabs = 0 } = getDataFromApiResponse(apiData);
                // setFieldValue(transferTypeField, transferType);
                setFieldValue(pickUpFrom, pickup);
                setFieldValue(dropAt, dropOf);
                setFieldValue(cabTypeField, cabType);
                setFieldValue(numberOfCabField, numberOfcabs);
            } catch (err) {
                console.error('This is the error state while saving', err);
            }
        },
        setCabTypeAndNumberOfCabs(state, action) {
            const { tabValue, apiData } = action?.payload;
            const prefilledReference = state?.data?.formStructures?.[tabValue]?.prefilledFormStructure;
            const formFieldGroups = prefilledReference?.form_field_groups || [];
            const transferFormField = getCodeValue(formFieldGroups, 'key', 'transfers', 'fields');

            const setFieldValue = (obj, value) => {
                if (!obj) return;
                obj.field.input_value = {
                    ...(obj?.field?.input_value || {}),
                    ...(value || {})
                };
            };

            const getDataFromApiResponse = (apiData = '') => {
                let cabType = '';
                let numberOfcabs = 0;

                if (apiData && typeof apiData === 'object' && Object?.keys(apiData)?.length > 0) {
                    cabType = {
                        label: apiData?.transferMode?.name || '',
                        value: apiData?.transferMode?.code || '',
                        key: apiData?.transferMode?.code || ''
                    };
                    numberOfcabs = {
                        label: apiData?.numberOfCabs?.toString() || '',
                        value: apiData?.numberOfCabs?.toString() || '',
                        key: apiData?.numberOfCabs?.toString() || ''
                    };
                }
                return {
                    cabType,
                    numberOfcabs
                };
            };

            const { cabType = '', numberOfcabs = 0 } = getDataFromApiResponse(apiData);

            const cabTypeField = transferFormField?.find((i) => i?.field?.key === 'cab_type');
            const numberOfCabField = transferFormField?.find((i) => i?.field?.key === 'number_of_cabs');

            setFieldValue(cabTypeField, cabType);
            setFieldValue(numberOfCabField, numberOfcabs);
        }
    }
});

// Reducer
export default slice.reducer;
export const {
    toggleOfflineComponent,
    updateHotelName,
    removeOfflineSightSeeing,
    addOfflineSightSeeing,
    findAndsetOfflineSightSeeingForHotelCard,
    saveOfflinehotelData,
    saveOfflineSightSeeingData,
    setOnlineHotel,
    setFormDataProviderTraceId,
    setQuoteTransferFromFields,
    setCabTypeAndNumberOfCabs
} = slice.actions;
// ----------------------------------------------------------------------

export const handleFormData =
    (formData = {}, quoteTabValue = null) =>
    () => {
        try {
            dispatch(slice.actions.setFormStructures({ formStructure: formData, quoteTabValue }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };

export function getSightseeingTypes(
    reqBody = {},
    value = [],
    setAll = false,
    sightseeingField = {},
    prefilledFormStructure = {},
    tabValue = 0
) {
    const url = `${ARYABHATTA_API_DOMAIN_V1}auto-quote/sightseeing-type`;
    return async () => {
        try {
            devConsolelog('fdfdsfd54545');
            dispatch(slice.actions.setIsLoading({ loadingField: 'isPrefillDataLoading', isLoading: true }));
            const apiResponse = await axios.post(url, reqBody, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response = {} } = data || {};
            const { results = {} } = response || {};
            const { sightSeeingsData = {} } = results || {};
            const { data: sightseeingDataArray = [] } = sightSeeingsData || {};
            const { name = '', code = '', sightSeeingRates = {} } = sightseeingDataArray?.[0];
            if (value?.length > 0 && !setAll) {
                let selectedTransferType = 0;
                const rateArr = Object.values(sightSeeingRates);
                const rateIdx = Object.keys(sightSeeingRates);
                rateArr?.forEach((item, index) => {
                    if (item?.selected) {
                        selectedTransferType = rateIdx?.[index];
                    }
                });
                const sightseeingObj = value && value[value?.length - 1];
                value[value.length - 1] = {
                    ...sightseeingObj,
                    transferTypeArray: Object.keys(sightSeeingRates),
                    transfer_type: Number(selectedTransferType)
                };
            } else {
                const sightseeingPrefilledArr = sightseeingField?.field?.input_value || [];
                sightseeingPrefilledArr?.forEach((item, index) => {
                    if (sightseeingDataArray?.[index]) {
                        const { sightSeeingRates = {} } = sightseeingDataArray?.[index] || {};
                        item.transferTypeArray = Object.keys(sightSeeingRates);
                    }
                });
                sightseeingField.field.input_value = sightseeingPrefilledArr;
                dispatch(handleFormData(prefilledFormStructure, tabValue));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        } finally {
            dispatch(slice.actions.setIsLoading({ loadingField: 'isPrefillDataLoading', isLoading: false }));
        }
    };
}

export function getFormBuilderData(
    destination = '',
    quoteType = '',
    quoteTabValue = null,
    isPrefillFlow = false,
    prefilledData = {},
    landOnlyQuote = false,
    isHhiQuote = ''
) {
    devConsolelog('ihhhii4324', isHhiQuote);
    dispatch(slice.actions.setIsLoading({ loadingField: 'isPrefillDataLoading', isLoading: true }));
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}holidays/form-builder/destination-forms${
            isHhiQuote ? '-v2' : ''
        }/?destination=${destination}&platform=aryabhatta&type=${quoteType}&hhi_form=${isHhiQuote}`;

        try {
            const apiResponse = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response = {} } = data || {};
            const { results = [] } = response || {};
            const formStructure = results[0] || {};
            devConsolelog('inside form structure', formStructure);
            if (!formStructure?.date_of_travel) {
                // formStructure.date_of_travel = format(new Date().setDate(new Date().getDate() + 14), 'yyyy-MM-dd');
                formStructure.date_of_travel = null;
                formStructure.traceId = null;
                formStructure.providerTraceId = null;
            }
            if (isPrefillFlow) {
                const formStructureObj = { originalFormStructure: formStructure, prefilledFormStructure: prefilledData, landOnlyQuote };
                (async () => {
                    if (prefilledData && checkIsKeyPresentInObject(prefilledData, 'transfers', 'object') && isHhiQuote) {
                        if (prefilledData.transfers) {
                            await dispatch(
                                setTransferData({
                                    type: ONLINE_TRANSFERS_CREATE_QUOTE,
                                    data: getTransfersDataFromApi(prefilledData.transfers),
                                    destinationCode: destination,
                                    tabValue: quoteTabValue
                                })
                            );
                        }
                    }
                })();
                devConsolelog('inside form data22', formStructure, results, formStructureObj);
                dispatch(slice.actions.setPrefillFormStructure({ formStructure: formStructureObj, quoteTabValue }));
                const {
                    destination_code: destinationCode = '',
                    form_field_groups: formFieldGroups = [],
                    date_of_travel: dateOfTravel = '',
                    rooms_guests: roomsGuests = []
                } = prefilledData || {};
                if (destinationCode !== 'desjkkh') {
                    const {
                        adult_count: adultCount = null,
                        child_count: childCount = null,
                        infant_count: infantCount = null
                    } = roomsGuests?.[0] || {};
                    let noOfNights = 0;
                    let areaCode = '';
                    let sightseeingField = {};
                    formFieldGroups.forEach((item) => {
                        const { key = '' } = item || {};
                        if (key === 'hotel_and_sightseeings') {
                            const { fields = {} } = item;
                            fields?.forEach((innerField) => {
                                const { field = '' } = innerField || {};
                                const { input_value: inputVal = {}, key = '' } = field;
                                devConsolelog('my new item22', field);
                                if (key === 'number_of_nights_1') {
                                    noOfNights = inputVal?.label;
                                }
                                if (key === 'area') {
                                    areaCode = inputVal?.key;
                                }
                                if (key === 'sightseeing') {
                                    sightseeingField = innerField;
                                }
                            });
                        }
                    });
                    if (!sightseeingField || isEmptyObject(sightseeingField)) return;
                    const { field = {} } = sightseeingField || {};
                    const { input_value: inputVal = [] } = field || {};
                    const endDate = addDays(new Date(dateOfTravel), Number(noOfNights));
                    const sightseeingCodesList = [];
                    inputVal?.forEach((item) => {
                        const { key = '' } = item || {};
                        sightseeingCodesList.push({ code: key });
                    });
                    const reqBody = {
                        areaCode,
                        destinationCode,
                        numberOfAdults: adultCount,
                        numberOfChild: childCount,
                        numberOfInfants: infantCount,
                        travelStartDate: dateOfTravel,
                        travelEndDate: dateOfTravel && format(new Date(endDate), 'yyy-MM-dd'),
                        selectedSightSeeings: sightseeingCodesList
                    };
                    await dispatch(getSightseeingTypes(reqBody, [], true, sightseeingField, prefilledData, quoteTabValue));
                } else {
                    dispatch(slice.actions.setIsLoading({ loadingField: 'isPrefillDataLoading', isLoading: false }));
                }
            } else {
                dispatch(slice.actions.setIsLoading({ loadingField: 'isPrefillDataLoading', isLoading: false }));
                dispatch(slice.actions.setOriginalFormStructure({ formStructure, quoteTabValue }));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isPrefillDataLoading', isLoading: false }));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getHotelIndex(newIdx = '') {
    return () => {
        try {
            dispatch(slice.actions.setHotelIndex(newIdx));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export const handleErrorValidation = (error, quoteTab) => () => {
    try {
        devConsolelog('inside err validation33', error);
        dispatch(slice.actions.hasErrValidation({ error, quoteTab }));
    } catch (err) {
        dispatch(slice.actions.hasError(err));
    }
};

export const handleNewAddedQuote =
    (addedQuote = {}) =>
    () => {
        try {
            dispatch(slice.actions.setNewAddedQuote(addedQuote));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };

export function getQuoteDetailsWithPriceBreakup(traceId = null, isHhiForm = false) {
    // use trace id instead dummy trace id
    const url = `${ARYABHATTA_API_DOMAIN_V1}holidays/form-builder/destination-forms${isHhiForm ? '-v2' : ''}/?trace_id=${traceId}`;
    dispatch(slice.actions.setIsLoading({ loadingField: 'isQuoteDetailsLoading', isLoading: true }));
    return async () => {
        try {
            const apiResponse = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response: quoteResults = {} } = data || {};
            const { response = {} } = quoteResults || {};
            devConsolelog('getQuoteDetailsWithPriceBreakup', data);
            dispatch(slice.actions.setQuoteDetails(response)); // use Results from api
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function handleAutoSuggest(reqBody = {}, key = null, type = null, index = null, setCustomResults = () => {}) {
    devConsolelog('inside auto-suggest', reqBody);
    const url = `${ARYABHATTA_API_DOMAIN_V1}auto-quote/autosuggest`;
    return async () => {
        try {
            const apiResponse = await axios.post(url, reqBody, {
                headers: {
                    source: 'mweb'
                }
            });
            const { response = {} } = apiResponse.data || {};
            const { results = [] } = response || {};
            const finalResults = results.map((result) => {
                const {
                    code = null,
                    name = '',
                    travsysAirportCode = null,
                    starRating = null,
                    type = 0,
                    area: { code: areaCode = '' } = {},
                    isTicketRequired = false
                } = result;
                const obj = {};
                obj.key = code || travsysAirportCode;
                obj.value = code || travsysAirportCode;
                obj.label = name;
                obj.sightseeing_type = type;
                obj.areaCode = areaCode;
                obj.isTicketRequired = isTicketRequired;
                if (starRating) obj.starRating = starRating;
                return obj;
            });
            dispatch(slice.actions.setAutoSuggestData({ results: finalResults, key, type, index }));
            setCustomResults(finalResults);
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export const handleLandOnly = (tabValue, isLandOnly) => {
    devConsolelog('inside tab val22', tabValue, isLandOnly);
    try {
        dispatch(slice.actions.setLandOnlyState({ tabValue, isLandOnly }));
    } catch {
        devConsolelog('err state', isLandOnly);
    }
};

export function getQuotesList(reqCode = null, tabIndex) {
    const url = `${ARYABHATTA_API_DOMAIN_V1}acm/requirements/${reqCode}/quotes`;
    dispatch(slice.actions.setIsLoading({ loadingField: 'isQuotesListLoading', isLoading: true }));
    return async () => {
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = response || {};
            const { results = [] } = data || {};
            const quotesListObj = {};
            results.forEach((quote, idx) => {
                const { code: quoteCode = null } = quote || {};
                const tabValue = results.length - idx;
                dispatch(handleFormData({ code: quoteCode }, tabValue));
                quotesListObj[results.length - idx] = quote;
            });
            dispatch(slice.actions.setSelectedTab(tabIndex));
            dispatch(slice.actions.setQuotesList(quotesListObj));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isQuotesListLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isQuotesListLoading', isLoading: false }));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function postQuoteRequirement(reqBody = null, isHhiForm = false, buyerEmail = '') {
    devConsolelog('reqBody34324', reqBody);
    dispatch(slice.actions.setIsLoading({ loadingField: 'isPostQuoteDataLoading', isLoading: true }));
    const url = `${ARYABHATTA_API_DOMAIN_V1}holidays/form-builder/destination-forms${isHhiForm ? '-v2' : ''}/`;
    return async () => {
        try {
            const apiResponse = await axios.post(url, reqBody, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response = {} } = data || {};
            const { trace_id: traceId = null, error = [] } = response || {};

            // remove this and uncomment above below code
            // const url = `/quote-preview/ebf6b2ff-41e9-4b42-a0b0-f689216ef3c1}`;
            // window.open(url, '_blank');

            if (error.length > 0) {
                const errors = error.map((item) => item);
                dispatch(slice.actions.setErrorsOfFormBuilder(errors));
            } else if (traceId) {
                dispatch(slice.actions.setErrorsOfFormBuilder([]));
                const url = isHhiForm
                    ? `/quote-preview/${traceId}?isHhiForm=true&buyerEmail=${buyerEmail}`
                    : `/quote-preview/${traceId}?buyerEmail=${buyerEmail}`;
                window.open(url, '_blank');
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            // dispatch(slice.actions.setIsLoading({ loadingField: 'isQuotesListLoading', isLoading: false }));
        }
        dispatch(slice.actions.setIsLoading({ loadingField: 'isPostQuoteDataLoading', isLoading: false }));
    };
}

export const handleAutoSuggestInputValues =
    (key = null, value = null, index = null, tabValue, type) =>
    () => {
        try {
            devConsolelog('handleAutoSuggestInputValues', tabValue, type, index);
            dispatch(slice.actions.setAutoSuggestInputValues({ key, value, index, tabValue, type }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };

export const handleAddHotel = (formStructureAfterAddingHotel, tabValue) => () => {
    try {
        dispatch(slice.actions.addHotelFormFieldGroups({ formStructureAfterAddingHotel, tabValue }));
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
};

export function getAutoSuggestRequirement(reqCode = null, type = null) {
    const url = `${ARYABHATTA_API_DOMAIN_V1}lms/requirements/auto-suggest/suggest/?code__completion=${reqCode}`;
    return async () => {
        try {
            const apiResponse = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response = [] } = data || {};
            dispatch(slice.actions.setAutoSuggestData({ results: response, type }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getDestinationCodeFromMemberList(destinationId = null) {
    const url = `${ARYABHATTA_API_DOMAIN_V1}holidays/memberlist/${destinationId}/destinations`;
    return async () => {
        try {
            const apiResponse = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response = {} } = data || {};
            dispatch(slice.actions.setDestinationDetails(response));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getHotelRooms(
    hotelCode = null,
    type,
    key,
    index,
    isChangeRoom = false,
    filterFieldsForRoom1 = {},
    filterFieldsForRoom2 = {},
    getRoomFields = () => {}
) {
    const url = `${ARYABHATTA_API_DOMAIN_V1}auto-quote/hotel/rooms?hotel_code=${hotelCode}`;
    return async () => {
        try {
            const apiResponse = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response = {} } = data || {};
            const { results = {} } = response || {};
            const { rooms = [] } = results || {};
            const dropdownData = rooms.map((room) => {
                const { name = '', code = null } = room || {};
                const obj = { label: name, value: code };
                return obj;
            });
            if (isChangeRoom) {
                devConsolelog('hotel changes', hotelCode);
                filterFieldsForRoom1.field.input_value = { value: dropdownData?.[0]?.value, label: dropdownData?.[0]?.label };
                if (filterFieldsForRoom2 && !isEmptyObject(filterFieldsForRoom2)) {
                    filterFieldsForRoom2.field.input_value = { value: dropdownData?.[0]?.value, label: dropdownData?.[0]?.label };
                }
                getRoomFields();
                return;
            }
            dispatch(slice.actions.setAutoSuggestData({ results: dropdownData, type, key, index }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getPopularHotels(destinationCode = null, type, key, index) {
    devConsolelog('hotel code 234', destinationCode);
    const url = `${ARYABHATTA_API_DOMAIN_V1}auto-quote/hotel?popular=true&destinationCode=${destinationCode}`;
    return async () => {
        try {
            const apiResponse = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response = {} } = data || {};
            const { results = {} } = response || {};
            const { data: hotelData = [] } = results || {};
            const dropdownData = hotelData.map((hotelData) => {
                const { name = '', code = null, auto_quote_area: { code: areaCode = '' } = {} } = hotelData || {};
                const obj = { label: name, value: code, areaCode };
                return obj;
            });
            dispatch(slice.actions.setAutoSuggestData({ results: dropdownData, type, key, index }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getPopularSightSeeings(destinationCode = null, areaCode = null, type, key, index) {
    devConsolelog('hotel code 234', destinationCode);
    const url = `${ARYABHATTA_API_DOMAIN_V1}auto-quote/sightseeings?popular=true&destinationCode=${destinationCode}`;
    return async () => {
        try {
            const apiResponse = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response = {} } = data || {};
            const { results: hotelData = [] } = response || {};
            const dropdownData = hotelData.map((hotelData) => {
                const { name = '', code = null, type = 0, isTicketRequired = false } = hotelData || {};
                const obj = { label: name, value: code, sightseeing_type: type, isTicketRequired };
                return obj;
            });
            devConsolelog('my dropdown data', dropdownData);
            dispatch(slice.actions.setAutoSuggestData({ results: dropdownData, type, key, index }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getAreas(destinationCode = '', page = 0, type, key, index, autoSuggestData, areasHasMore, showAll) {
    const url = showAll
        ? `${ARYABHATTA_API_DOMAIN_V1}auto-quote/areas?all=true&destinationCode=${destinationCode}`
        : `${ARYABHATTA_API_DOMAIN_V1}auto-quote/areas?all=true&destinationCode=${destinationCode}&page=${page}&size=20`;
    return async () => {
        try {
            const apiResponse = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response = {} } = data || {};
            const { results: areasData = [] } = response || {};
            const dropdownData = areasData?.map((areaData) => {
                const { name = '', code = null } = areaData || {};
                const obj = { label: name, value: code };
                return obj;
            });
            if (areasData && areasData.length === 0) {
                areasHasMore.current = false;
                return;
            }
            dispatch(slice.actions.setAutoSuggestData({ results: dropdownData, type, key, index }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getCabTypes(key, type, index, destinationCode, setCustomResults = () => {}) {
    const url = `${ARYABHATTA_API_DOMAIN_V1}auto-quote/transfers/transfer-modes?destinationCode=${destinationCode}`;
    return async () => {
        try {
            const apiResponse = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response = {} } = data || {};
            const { results = [] } = response || {};
            const modifiedResults = results.map((result) => {
                const { code = null, name = '' } = result || {};
                const obj = {
                    key: code,
                    value: code,
                    label: name
                };
                return obj;
            });
            dispatch(slice.actions.setAutoSuggestData({ results: modifiedResults, type, key, index }));
            setCustomResults(modifiedResults);
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getPrefillQuoteData(quoteCode, tabValue, isHhiQuote = '') {
    const url = `${ARYABHATTA_API_DOMAIN_V1}holidays/form-builder/destination-forms${isHhiQuote ? '-v2' : ''}/?quote_code=${quoteCode}`;
    dispatch(slice.actions.setIsLoading({ loadingField: 'isPrefillDataLoading', isLoading: true }));
    return async () => {
        try {
            const apiResponse = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response = {} } = data || {};
            const { response: formData = {}, trace_id: traceId = '' } = response || {};
            formData.traceId = traceId;
            formData.providerTraceId = null;
            formData.isDataPrefilled = true;

            devConsolelog('bdjkasd343', formData);

            const {
                destination_code: destinationCode = null,
                form_type: formType = null,
                land_only_quote: landOnlyQuote = false
            } = formData || {};
            if (destinationCode && formType) {
                devConsolelog('inside formData33', formData, landOnlyQuote);
                dispatch(getFormBuilderData(destinationCode, formType?.toLowerCase(), tabValue, true, formData, landOnlyQuote, isHhiQuote));
            } else {
                dispatch(slice.actions.setIsLoading({ loadingField: 'isPrefillDataLoading', isLoading: false }));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isPrefillDataLoading', isLoading: false }));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getQuoteDetails(hotelCode) {
    const url = `${ARYABHATTA_API_DOMAIN_V1}auto-quote/hotel?hotel_code=${hotelCode}&detail=true`;
    return async () => {
        try {
            const apiResponse = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response = {} } = data || {};
            const { results = {} } = response || {};
            dispatch(slice.actions.setHotelDetailsData(results));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function shareQuoteWithAgent(reqBody = {}, setIsShareModalOpen = () => {}, setModalSuccess = () => {}, isHhiForm = false) {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isShareWithAgentLoading', isLoading: true }));
    const url = `${ARYABHATTA_API_DOMAIN_V1}holidays/form-builder/destination-forms${isHhiForm ? '-v2' : ''}/`;
    return async () => {
        try {
            const apiResponse = await axios.post(url, reqBody, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { response = {} } = data || {};
            const { message = '', quote_code: quoteCode = '' } = response || {};
            dispatch(slice.actions.setSuccessMessage(message));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isShareWithAgentLoading', isLoading: false }));
            setIsShareModalOpen(false);
            // dispatch(
            //     openSnackbar({
            //         open: true,
            //         message,
            //         variant: 'alert',
            //         alert: {
            //             color: 'success'
            //         },
            //         anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            //         transition: 'SlideLeft',
            //         close: true
            //     })
            // );
            setModalSuccess({ status: true, quoteCode });
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isShareWithAgentLoading', isLoading: false }));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            setIsShareModalOpen(false);
        }
    };
}

export function sumitAIQuoteRating(requirementCode = '', quoteCode = '', rating = 0, tabIndex = null) {
    devConsolelog('response34editBookingDetail', tabIndex);
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/requirements/${requirementCode}/quotes/${quoteCode}`;
        const updatedData = {
            ai_quote_rating: rating
        };
        try {
            const response = await axios.patch(url, updatedData, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Rating Submitted',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            dispatch(getQuotesList(requirementCode, tabIndex));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            devConsolelog('inside error has error patch data22');
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}
export const handleHotelNamePrefill =
    (offlineComponentPayload, closeOfflineModal, tabValue, hotelIndex, isOfflineSubmit = true, resetForm = () => {}, isHhiForm = false) =>
    async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}${AUTO_QUOTE}/${OFFLINE_COMPONENT_URL}/${HOTELS}`;
        const payload = [
            {
                ...offlineComponentPayload,
                area: offlineComponentPayload?.area?.value,
                supplier: offlineComponentPayload?.supplier?.code,
                supplierEmail: offlineComponentPayload?.supplier?.email,
                supplierName: offlineComponentPayload?.supplier?.name
            }
        ];

        try {
            const response = await axios.post(url, payload, {
                headers: {
                    source: 'mweb'
                }
            });
            const { results = [] } = response?.data?.response || {};
            const { code, roomCode } = results?.[0] || {};
            devConsolelog('response--->', response);

            devConsolelog('payload', payload);
            const updatedPayload = { ...offlineComponentPayload, code, roomCode };
            dispatch(
                slice.actions.updateHotelName({ offlineComponentPayload: updatedPayload, tabValue, hotelIndex, isOfflineSubmit, isHhiForm })
            );
            resetForm();
            closeOfflineModal();
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Offline Hotel Added',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            devConsolelog('inside error has error patch data22', errorObj);
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };

export const confirmDeleteOfflineHotel =
    (
        offlineComponentPayload,
        closeOfflineModal,
        tabValue,
        hotelIndex,
        resetToOriginalState = true,
        isOfflineSubmit = false,
        isHhiForm = false
    ) =>
    async () => {
        try {
            dispatch(
                slice.actions.updateHotelName({
                    offlineComponentPayload,
                    tabValue,
                    hotelIndex,
                    resetToOriginalState,
                    isOfflineSubmit,
                    isHhiForm
                })
            );
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Offline Hotel removed',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            closeOfflineModal();
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
export const handleAddOfflineSightSeeing = (
    closeSightSeeingModal = () => {},
    tabValue,
    hotelIndex,
    offlineSightSeeingData = [],
    resetForm = () => {}
) => {
    const url = `${ARYABHATTA_API_DOMAIN_V1}${AUTO_QUOTE}/${OFFLINE_COMPONENT_URL}/${SIGHT_SEEING}`;
    const payload = [...(offlineSightSeeingData || [])];
    return async () => {
        try {
            const response = await axios.post(url, payload, {
                headers: {
                    source: 'mweb'
                }
            });
            const { results = [] } = response?.data?.response || {};
            const codesArr = results?.map((item) => item?.code);
            const setPayload = offlineSightSeeingData?.map((item, idx) => ({
                label: item?.name,
                value: codesArr?.[idx],
                key: codesArr?.[idx],
                transfer_type: TRANSFERS_TYPE?.find((type) => type?.name === item?.transferType)?.value,
                sightseeing_type: 0,
                isOfflineComponent: item?.isOfflineComponent,
                prefillSightSeeing: item?.prefillSightSeeing,
                remarks: item?.remarks || ''
            }));

            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Offline SightSeeing Added',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.findAndsetOfflineSightSeeingForHotelCard({ tabValue, hotelIndex, offlineSightSeeingData: setPayload }));
            closeSightSeeingModal();
            resetForm();
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            devConsolelog('inside error has error patch data22', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
};

export const getOfflineHotelApiData = (hotelCode = '') => {
    const url = `${ARYABHATTA_API_DOMAIN_V1}${AUTO_QUOTE}/${OFFLINE_COMPONENT_URL}/${HOTELS}?code=${hotelCode}`;
    return async () => {
        try {
            dispatch(slice.actions.setOfflineHotelDataLoading(true));
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { results = [] } = response?.data?.response || {};
            dispatch(slice.actions.saveOfflinehotelData(results?.[0] || {}));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            devConsolelog('inside error has error patch data22', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(slice.actions.setOfflineHotelDataLoading(false));
        }
    };
};

export const getOfflineSightSeeingApiData = (codeArr = []) => {
    let url = `${ARYABHATTA_API_DOMAIN_V1}${AUTO_QUOTE}/${OFFLINE_COMPONENT_URL}/${SIGHT_SEEING}`;
    url = convertUrl(url, codeArr);
    return async () => {
        try {
            dispatch(slice.actions.setOfflineSightSeeingDataLoading(true));
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            const { results = [] } = response?.data?.response || {};
            dispatch(slice.actions.saveOfflineSightSeeingData(results || []));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            devConsolelog('inside error has error patch data22', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(slice.actions.setOfflineSightSeeingDataLoading(false));
        }
    };
};

export function updateVipTravellerStatus({
    quoteCode = '',
    requirementCode = '',
    isVip = false,
    tabValue = 0,
    prefilledFormStructure = {}
}) {
    return async (dispatch) => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}holidays/update-quote/`;

        const requestBody = {
            quote_code: quoteCode,
            requirement_code: requirementCode,
            is_vip: isVip
        };

        try {
            await axios.patch(url, requestBody, {
                headers: { source: 'mweb' }
            });

            const newPrefilledFormStructure = copyOfObject(prefilledFormStructure);
            newPrefilledFormStructure.is_vip = isVip;
            dispatch(handleFormData(newPrefilledFormStructure, tabValue));

            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Quote successfully updated',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: false
                })
            );
        }
    };
}
